import { withFinancialMetricsAccess } from "@crm-src/components/page-permission-guard"
import { useState, useEffect } from "react"
import { usePermissions } from "@crm-src/hooks/usePermissions"
import { useAppSelector } from "@crm-src/lib/store"
import { financialApi } from "@crm-src/lib/api"
import { useToast } from "@crm-src/hooks/use-toast"
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { 
  IndividualPerformanceData,
  PeriodType
} from "@crm-src/types/financial-metrics"

// Import components
import { Box } from "@mui/material"
import { TimeRangeSelector } from "@crm-src/components/financial-metrics/time-range-selector"
import { PDFExportButton } from "@crm-src/components/financial-metrics/pdf-export-button"
import { SalespersonOwnView } from "@crm-src/components/financial-metrics/salesperson-own-view"
import { PageHeader } from "@/components/ui/page-header"

function FinancialMetricsPage() {
  // State
  const [period, setPeriod] = useState<PeriodType>('1M')
  const [individualData, setIndividualData] = useState<IndividualPerformanceData | null>(null)
  const [isLoadingIndividual, setIsLoadingIndividual] = useState(true)
  
  const { canAccessAdmin } = usePermissions()
  const { toast } = useToast()
  const navigate = useNavigate()
  const user = useAppSelector((state) => state.auth.user)
  const isAdmin = canAccessAdmin()

  // Redirect admin to company page
  useEffect(() => {
    if (isAdmin) {
      navigate(`/crm/financial-metrics/company?period=${period}`)
    }
  }, [isAdmin, navigate])

  // Load individual data for salesperson
  useEffect(() => {
    if (!isAdmin && user?.id) {
      loadIndividualDetails(user.id)
    }
  }, [period, isAdmin, user?.id])

  const loadIndividualDetails = async (userId: number) => {
    try {
      setIsLoadingIndividual(true)
      const response = await financialApi.getSalespersonDetails(userId.toString(), { period })
      setIndividualData(response.data)
    } catch (error: any) {
      console.error('Error loading individual details:', error)
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to load salesperson details",
        variant: "destructive",
      })
    } finally {
      setIsLoadingIndividual(false)
    }
  }

  // Render for non-admin (salesperson)
  if (!isAdmin) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader
          title="My Performance"
          description="Track your sales metrics and achievements"
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TimeRangeSelector selected={period} onChange={setPeriod} />
              <PDFExportButton period={period} userId={user?.id?.toString()} variant="outline" />
            </Box>
          }
        />

        {/* Salesperson Own View */}
        {individualData && (
          <SalespersonOwnView
            performance={individualData.performance}
            monthlyBreakdown={individualData.monthly_breakdown}
            isLoading={isLoadingIndividual}
          />
        )}
      </Box>
    )
  }

  // For admin, this will be redirected to company page
  return null
}

export default withFinancialMetricsAccess(FinancialMetricsPage)
